import { forwardRef, type HTMLAttributes } from 'react'
import { Avatar as BaseAvatar } from '@base-ui/react/avatar'
import { User } from 'lucide-react'
import { cn } from '@/lib/utils'
import './avatar.css'

/**
 * Avatar sizes. xs (24px) marks something as someone's inside a small space, sm (28px) leads a row of text such as a
 * chat message, compact (36px) fits inside an IconButton, default matches a 44px control, large leads a profile.
 */
export type AvatarSize = 'xs' | 'sm' | 'compact' | 'default' | 'large'

/**
 * Every disc: the theme's neutral one, the person tints (flat tints of the brand palette over white: pink, green, peach,
 * grey-blue), and `organization`, the brand's solid yellow for an organisation without a logo. There is no yellow tint:
 * yellow is the organisation's. Identity, never state.
 */
export const AVATAR_TONES = ['neutral', 'pink', 'green', 'peach', 'grey-blue', 'organization'] as const
export type AvatarToneName = (typeof AVATAR_TONES)[number]
/**
 * The person tones `auto` picks from: the pastel tints, never the neutral disc (which reads as nobody in particular)
 * and never yellow, which stays the organisation's.
 */
export const AVATAR_PERSON_TONES = ['pink', 'green', 'peach', 'grey-blue'] as const
export type AvatarPersonTone = (typeof AVATAR_PERSON_TONES)[number]
/** A tone, or `auto` to pick one of the person tones from `toneKey`. */
export type AvatarTone = AvatarToneName | 'auto'

export type AvatarProps = Omit<HTMLAttributes<HTMLSpanElement>, 'children'> & {
  /** The person's name, username or email. Initials come from it. Without one the avatar shows a person glyph. */
  name?: string | null
  /** The profile photo. While it loads, or if it fails, the initials show instead. */
  src?: string | null
  size?: AvatarSize
  /** The disc behind the initials. A shown photo or logo sits on the neutral disc instead (a transparent logo never
   *  lands on a tint), and so does an organisation's logo while it loads (no yellow flash before it). `auto` picks a
   *  person tone from `toneKey`; `organization` is the brand's solid yellow. */
  tone?: AvatarTone
  /** What `auto` picks the tone from: the same value everywhere the person appears (in the app, `personToneKey`). */
  toneKey?: string | null
  /** A green ring outside the disc. Only a mark: say live in words, visible nearby or announced in `label`. */
  live?: boolean
  /** Announces the avatar as an image with this name. Leave it out when the name or the control's label is
   *  already nearby, so the person isn't read twice. */
  label?: string
}

/** Up to two initials: first and last word of a name, or the first character of a username or email. */
export function initialsFor(name?: string | null) {
  const words = (name ?? '').trim().replace(/^@/, '').split(/\s+/).filter(Boolean)
  if (words.length === 0) return ''
  if (words.length === 1) return Array.from(words[0])[0].toUpperCase()
  return (Array.from(words[0])[0] + Array.from(words[words.length - 1])[0]).toUpperCase()
}

/**
 * A person tone for a key, the same one every time and everywhere: a 32-bit FNV-1a hash of the key, finished with
 * MurmurHash3's avalanche (fmix32), picks one of `AVATAR_PERSON_TONES`. Without the finish, the hash's low two bits
 * (the ones the pick reads) would depend only on the low two bits of each character. Without a key, neutral.
 */
export function avatarToneFor(key?: string | null): AvatarPersonTone | 'neutral' {
  if (!key) return 'neutral'
  let hash = 0x811c9dc5
  for (let index = 0; index < key.length; index++) {
    hash ^= key.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }
  hash ^= hash >>> 16
  hash = Math.imul(hash, 0x85ebca6b)
  hash ^= hash >>> 13
  hash = Math.imul(hash, 0xc2b2ae35)
  hash ^= hash >>> 16
  return AVATAR_PERSON_TONES[(hash >>> 0) % AVATAR_PERSON_TONES.length]
}

/** A person's photo in a circle, with their initials while it loads or when there is none. Not interactive. */
export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  { name, src, size = 'default', tone = 'neutral', toneKey, live = false, label, className, ...props },
  ref,
) {
  const initials = initialsFor(name)
  const resolvedTone = tone === 'auto' ? avatarToneFor(toneKey) : tone
  const tinted = resolvedTone !== 'neutral'
  return (
    <BaseAvatar.Root
      ref={ref}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      data-tone={tinted ? resolvedTone : undefined}
      data-live={live || undefined}
      // `loading` while a photo or logo is on its way (Base UI mounts the image once it has loaded; a failed one falls
      // back to the initials): an organisation's disc waits on neutral, so its yellow never flashes before the logo.
      className={({ imageLoadingStatus }) =>
        cn(
          'play-avatar',
          size !== 'default' && `play-avatar--${size}`,
          tinted && `play-avatar--${resolvedTone}`,
          live && 'play-avatar--live',
          src && imageLoadingStatus !== 'loaded' && imageLoadingStatus !== 'error' && 'play-avatar--loading',
          className,
        )
      }
      {...props}
    >
      {src && <BaseAvatar.Image src={src} alt="" className="play-avatar__image" />}
      <BaseAvatar.Fallback className="play-avatar__fallback">
        {initials || <User aria-hidden="true" />}
      </BaseAvatar.Fallback>
    </BaseAvatar.Root>
  )
})
